import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CreateClientInput } from '@/api/types';

interface ClientFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateClientInput) => void;
  isSaving: boolean;
  initialData?: Partial<CreateClientInput>;
  title?: string;
}

export function ClientFormDrawer({
  isOpen,
  onClose,
  onSave,
  isSaving,
  initialData,
  title,
}: Readonly<ClientFormDrawerProps>) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [regCode, setRegCode] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name ?? '');
      setAddress(initialData?.address ?? '');
      setPhone(initialData?.phone ?? '');
      setEmail(initialData?.email ?? '');
      setRegCode(initialData?.reg_code ?? '');
      setVatNumber(initialData?.vat_number ?? '');
      setNotes(initialData?.notes ?? '');
    }
  }, [isOpen, initialData]);

  const handleSubmit = () => {
    const data: CreateClientInput = { name: name.trim() };
    if (address.trim()) data.address = address.trim();
    if (phone.trim()) data.phone = phone.trim();
    if (email.trim()) data.email = email.trim();
    if (regCode.trim()) data.reg_code = regCode.trim();
    if (vatNumber.trim()) data.vat_number = vatNumber.trim();
    if (notes.trim()) data.notes = notes.trim();
    onSave(data);
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{title ?? t('clients.saveNew')}</SheetTitle>
          <SheetDescription className="sr-only">{t('clients.detail')}</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 p-4">
          <div>
            <label className="text-sm font-medium">{t('clients.name')} *</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="client-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('clients.address')}</label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">{t('clients.phone')}</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" />
          </div>
          <div>
            <label className="text-sm font-medium">{t('clients.email')}</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </div>
          <div>
            <label className="text-sm font-medium">{t('clients.regCode')}</label>
            <Input value={regCode} onChange={(e) => setRegCode(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">{t('clients.vatNumber')}</label>
            <Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">{t('clients.notes')}</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!name.trim() || isSaving}
              className="flex-1"
              data-testid="client-save-button"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
